import { ADVISORY_TYPE_OPTIONS } from "../../advisories/utils/advisoryType";

const VALID_ADVISORY_TYPES = ADVISORY_TYPE_OPTIONS.map(({ value }) => value);

/**
 * Server-side default for a fresh subscription: incident and maintenance email are on,
 * advisories are opted out (empty array). Mirrors the backend's own default so a
 * first-time subscriber's checkboxes match what they'd get if they saved nothing.
 */
export const DEFAULT_SUBSCRIPTION_TYPES = Object.freeze({
    incident: true,
    maintenance: true,
    advisoryTypes: Object.freeze([]),
});

/**
 * GET /subscriptions/{email} returns event_type_preferences: { incident, maintenance,
 * advisory_types }. Falls back to the default shape when the field is absent (e.g. a
 * 404/empty response before any subscription exists).
 */
export const hydrateSubscriptionTypes = (subscriptionsData) => {
    const preferences = subscriptionsData?.event_type_preferences;

    if (!preferences || typeof preferences !== "object") {
        return { ...DEFAULT_SUBSCRIPTION_TYPES };
    }

    const advisoryTypes = preferences.advisory_types;

    return {
        incident: !!preferences.incident,
        maintenance: !!preferences.maintenance,
        advisoryTypes: Array.isArray(advisoryTypes)
            ? advisoryTypes.filter((t) => VALID_ADVISORY_TYPES.includes(t))
            : [],
    };
};

/**
 * Builds the event_type_preferences body for POST /subscriptions. Always sent -- the
 * backend now understands this field, so there is no "omit when default" short-circuit.
 * An empty advisory_types array is itself meaningful: it means "opted out of advisories".
 */
export const buildEventTypePreferences = (subscriptionTypes) => ({
    event_type_preferences: {
        incident: !!subscriptionTypes?.incident,
        maintenance: !!subscriptionTypes?.maintenance,
        advisory_types: (subscriptionTypes?.advisoryTypes || []).filter((t) =>
            VALID_ADVISORY_TYPES.includes(t),
        ),
    },
});

/**
 * Human-readable summary for the read-only review modal, e.g.
 * "Incidents, Maintenance, Advisories (Security, Release)".
 */
export const formatSubscriptionTypes = (subscriptionTypes) => {
    if (!subscriptionTypes) return "None";

    const parts = [];
    if (subscriptionTypes.incident) parts.push("Incidents");
    if (subscriptionTypes.maintenance) parts.push("Maintenance");

    const labels = ADVISORY_TYPE_OPTIONS
        .filter(({ value }) => subscriptionTypes.advisoryTypes?.includes(value))
        .map(({ label }) => label);
    if (labels.length) {
        parts.push(`Advisories (${labels.join(", ")})`);
    }

    return parts.length ? parts.join(", ") : "None";
};
